import { FC, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { getShippingAddressById } from '../../helpers/checkoutHelpers';
import {
    trackHasTaxDocsForOtherStateViewed,
    trackNoTaxDocsViewed,
    trackNoTaxDocsClick,
    trackHasTaxDocsForOtherStateClick,
} from '../../shared/helpers/taxExemptTracking';
import {
    getValidTaxDocuments,
    getValidTaxDocumentForState,
    InvalidTaxDocumentText,
    LearnMoreLink,
} from '../../shared/helpers/taxExemptHelpers';
import CheckoutTaxExemptionCheckbox from '../shared/CheckoutTaxExemptionCheckbox';

import styles from './styles/CheckoutTaxExemption.scss';

import { CheckoutTaxExemption_buyer$data } from './__generated__/CheckoutTaxExemption_buyer.graphql';
import { CheckoutTaxExemption_transaction$data } from './__generated__/CheckoutTaxExemption_transaction.graphql';
import { CheckoutTaxExemption_viewer$data } from './__generated__/CheckoutTaxExemption_viewer.graphql';

type Props = {
    isComputing: boolean;
    shippingAddressId: string;
    buyer: CheckoutTaxExemption_buyer$data;
    transaction: CheckoutTaxExemption_transaction$data;
    viewer: CheckoutTaxExemption_viewer$data;
};

const CheckoutTaxExemption: FC<Props> = props => {
    const { viewer, isComputing, shippingAddressId, transaction, buyer } = props;
    const canBeTaxExempt = !!transaction?.canBeTaxExempt;
    const shippingAddress = getShippingAddressById({ buyer, shippingAddressId });
    const selectedUSState = shippingAddress?.state;
    const isShippingToUs = shippingAddress?.country === 'US';
    const hasSelectedUSState = !!selectedUSState;
    const taxDocuments = getValidTaxDocuments({ buyer });
    const hasTaxDocuments = taxDocuments.length > 0;
    const hasTaxDocumentForState = !!getValidTaxDocumentForState({
        buyer,
        usState: selectedUSState || '',
    });

    useEffect(() => {
        if (hasSelectedUSState) {
            if (!hasTaxDocuments) {
                trackNoTaxDocsViewed('checkout');
            } else if (!hasTaxDocumentForState) {
                trackHasTaxDocsForOtherStateViewed('checkout');
            }
        }
    }, [hasTaxDocumentForState, hasSelectedUSState, hasTaxDocuments, shippingAddressId]);

    if (!hasSelectedUSState || (isShippingToUs && viewer.taxExemptCertificateUploadFF)) {
        return null;
    }

    let content = null;
    if (!hasTaxDocumentForState) {
        const clickTrackingFn = hasTaxDocuments
            ? trackHasTaxDocsForOtherStateClick
            : trackNoTaxDocsClick;
        content = (
            <div className={styles.invalidDoc}>
                *
                <div className={styles.invalidDocText}>
                    <InvalidTaxDocumentText usState={shippingAddress?.displayState || ''} />
                    <LearnMoreLink onClick={() => clickTrackingFn('checkout')} />
                </div>
            </div>
        );
    } else {
        content = (
            <CheckoutTaxExemptionCheckbox
                transaction={transaction}
                viewer={viewer}
                isComputing={isComputing}
            />
        );
    }

    if (hasTaxDocumentForState && !isComputing && !canBeTaxExempt) {
        return null;
    }
    return <div className={styles.container}>{content}</div>;
};

type ReduxPayload = {
    transaction: {
        shippingAddressId: string;
    };
};

const ConnectedCheckoutTaxExemption = connect(({ transaction }: ReduxPayload) => {
    return {
        shippingAddressId: transaction.shippingAddressId,
    };
})(CheckoutTaxExemption);

export default createFragmentContainer(ConnectedCheckoutTaxExemption, {
    viewer: graphql`
        fragment CheckoutTaxExemption_viewer on Viewer {
            taxExemptCertificateUploadFF: featureFlag(feature: "taxExemptCertificateUpload")
            ...CheckoutTaxExemptionCheckbox_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutTaxExemption_buyer on User {
            ...taxExemptHelpers_buyer
            ...checkoutHelpers_buyer
        }
    `,
    transaction: graphql`
        fragment CheckoutTaxExemption_transaction on Transaction {
            serviceId
            canBeTaxExempt
            ...CheckoutTaxExemptionCheckbox_transaction
        }
    `,
});
